// useChatSnapshot — synchronously hydrate the chat thread from a
// versioned `localStorage` cache, then reconcile with the backend via
// the `chat_snapshot_if_changed` RPC. Renders the cached history
// BEFORE the first network round-trip so the user never sees an empty
// thread; replaces the cached arrays only when the server reports
// `changed: true`.
//
// Design notes
// ------------
//   * The cache module (`chatCache.ts`) owns read/write/versioning.
//     This hook only orchestrates: load → ask → react.
//   * The hook intentionally keeps the in-memory array references
//     stable when `changed: false`. View code relies on this so a
//     "still the same history" round-trip doesn't reset scroll
//     position or re-trigger the signed-URL effect.
//   * Stale responses (the user navigated to a different thread mid-
//     fetch) are dropped at the `.then` boundary using a per-effect
//     "key" token. The latest call wins, older ones are no-ops.
//   * The RPC response is treated as untrusted input. A malformed
//     payload sets `error` but does NOT clobber the cached arrays or
//     the on-disk cache. (Per the parent directive: "tolerate
//     malformed payload as an error without deleting a still-valid
//     cache.")
//   * Realtime updates for `chat_messages` and `message_reactions`
//     both call `refresh()`. The view does not need to know which
//     table changed — the hash on the server flips for any mutation.
//   * Successful local writes (composer send, attachment insert,
//     reaction toggle) also call `refresh()` so a realtime outage
//     doesn't strand the user's own message at the top of the list.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  isChatMessageRows,
  isChatReactionRows,
  loadCachedSnapshot,
  saveCachedSnapshot,
  snapshotFromResponse,
  type ChatMessageRow,
  type ChatMessageReactionRow,
  type ChatSnapshotResponse,
} from "./chatCache";
import { supabase, isAuthConfigured } from "./supabase";

/** Mirror of the RPC's p_limit default. The backend caps at 1..200,
 *  but we pass the same constant so the request and the docstring
 *  stay in lockstep. */
const SNAPSHOT_LIMIT = 200;

export type ChatSnapshotState = {
  messages: ChatMessageRow[];
  reactions: ChatMessageReactionRow[];
  /** Backend's current SHA-256 over the selected rows. Empty string
   *  until the first successful round-trip; otherwise usable as
   *  `p_known_hash` on the next call. */
  hash: string;
  /** True until either the cache or a successful RPC has populated
   *  the state. View code can use this to gate a top-of-screen
   *  spinner; the cache makes the typical case instant. */
  loading: boolean;
  /** Last error message from the RPC, or null. Does NOT clear
   *  `messages` / `reactions` — those keep showing the cache. */
  error: string | null;
  /** Re-run the `chat_snapshot_if_changed` RPC. Use this on realtime
   *  events and after a local write completes. */
  refresh: () => void;
};


/**
 * Read the chat thread for `(userId, threadId)`. Both ids must be
 * non-empty for the hook to do anything; while either is null the
 * hook stays idle and returns an empty, non-loading state.
 */
export function useChatSnapshot(
  userId: string | null,
  threadId: string | null,
): ChatSnapshotState {
  // We synchronously seed from the cache so the first render already
  // shows history. This is intentional — useState's lazy init runs
  // before the first paint, so React commits with the cached arrays
  // in place. The RPC then runs in an effect and may replace the
  // arrays on a `changed: true` response.
  const initial = readInitialSnapshot(userId, threadId);
  const [messages, setMessages] = useState<ChatMessageRow[]>(initial.messages);
  const [reactions, setReactions] = useState<ChatMessageReactionRow[]>(initial.reactions);
  const [hash, setHash] = useState<string>(initial.hash);
  const [loading, setLoading] = useState<boolean>(initial.loading);
  const [error, setError] = useState<string | null>(null);

  // Latest-key ref: a stale response (user navigated to a different
  // thread while the RPC was in flight) must not overwrite the new
  // thread's state. The ref pattern is cheaper than an effect-cancel
  // token because we don't need to abort the network — we just
  // ignore the result.
  const currentKey = useRef<string>(keyOf(userId, threadId));
  // Tick is incremented by `refresh()` to retrigger the effect.
  const [tick, setTick] = useState(0);

  // When the (user, thread) key changes, swap the in-memory state
  // over to the new cache slot. The `useState` initialisers above
  // only run on the very first render, so we need this effect to
  // keep the cache-as-source-of-truth invariant on subsequent
  // navigations.
  useEffect(() => {
    const next = readInitialSnapshot(userId, threadId);
    setMessages(next.messages);
    setReactions(next.reactions);
    setHash(next.hash);
    setLoading(next.loading);
    setError(null);
    currentKey.current = keyOf(userId, threadId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, threadId]);

  const fetchOnce = useCallback(async (knownHash: string) => {
    if (!isAuthConfigured() || !userId || !threadId) {
      setLoading(false);
      return;
    }
    const myKey = keyOf(userId, threadId);
    try {
      const { data, error } = await supabase.rpc("chat_snapshot_if_changed", {
        p_thread_id: threadId,
        p_known_hash: knownHash.length > 0 ? knownHash : undefined,
        p_limit: SNAPSHOT_LIMIT,
      });
      if (currentKey.current !== myKey) return;
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      const parsed = parseSnapshotResponse(data);
      if (!parsed.ok) {
        setError(parsed.reason);
        setLoading(false);
        return;
      }
      if (!parsed.response.changed) {
        setError(null);
        setLoading(false);
        return;
      }
      const next = snapshotFromResponse(parsed.response);
      if (!next) {
        setError("snapshot response missing rows");
        setLoading(false);
        return;
      }
      setMessages(next.messages);
      setReactions(next.reactions);
      setHash(next.hash);
      setError(null);
      setLoading(false);
      saveCachedSnapshot(userId, threadId, next);
    } catch (error) {
      if (currentKey.current !== myKey) return;
      setError(error instanceof Error ? error.message : String(error));
      setLoading(false);
    }
  }, [userId, threadId]);

  useEffect(() => {
    if (!userId || !threadId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchOnce(hash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, userId, threadId]);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  return { messages, reactions, hash, loading, error, refresh };
}

// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------

function keyOf(userId: string | null, threadId: string | null): string {
  return `${userId ?? ""}::${threadId ?? ""}`;
}

function readInitialSnapshot(
  userId: string | null,
  threadId: string | null,
): { messages: ChatMessageRow[]; reactions: ChatMessageReactionRow[]; hash: string; loading: boolean } {
  // Calling loadCachedSnapshot during render is safe — it's
  // synchronous, try/catch wrapped, and never throws. React 18+
  // allows reading from `localStorage` during render.
  if (!userId || !threadId) {
    return { messages: [], reactions: [], hash: "", loading: false };
  }
  const cached = loadCachedSnapshot(userId, threadId);
  if (cached) {
    return {
      messages:  cached.messages,
      reactions: cached.reactions,
      hash:      cached.hash,
      loading:   true, // we'll still ask the RPC on mount to confirm
    };
  }
  return { messages: [], reactions: [], hash: "", loading: true };
}

type ParseResult =
  | { ok: true; response: ChatSnapshotResponse }
  | { ok: false; reason: string };

function parseSnapshotResponse(value: unknown): ParseResult {
  if (!isObject(value)) {
    return { ok: false, reason: "snapshot response is not an object" };
  }
  if (typeof value.hash !== "string" || value.hash.length === 0) {
    return { ok: false, reason: "snapshot response missing hash" };
  }
  if (typeof value.changed !== "boolean") {
    return { ok: false, reason: "snapshot response missing changed flag" };
  }
  if (value.changed) {
    if (!isChatMessageRows(value.messages)) {
      return { ok: false, reason: "snapshot response has invalid messages" };
    }
    if (!isChatReactionRows(value.reactions)) {
      return { ok: false, reason: "snapshot response has invalid reactions" };
    }
    return {
      ok: true,
      response: {
        hash:     value.hash,
        changed:  true,
        messages: value.messages,
        reactions: value.reactions,
      },
    };
  }
  return {
    ok: true,
    response: { hash: value.hash, changed: false },
  };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
